"use client";

import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { cn } from "@/lib/utils";

export interface SegmentedControlOption<TValue extends string> {
  value: TValue;
  label: string;
  disabled?: boolean;
}

/**
 * Single-select pill group (calendar scale, timeframe, timeline scale). One
 * option is always selected: re-pressing the active option keeps it.
 */
export function SegmentedControl<TValue extends string>({
  value,
  onValueChange,
  options,
  className,
  "aria-label": ariaLabel,
}: {
  value: TValue;
  onValueChange: (value: TValue) => void;
  options: readonly SegmentedControlOption<TValue>[];
  className?: string;
  "aria-label": string;
}) {
  return (
    <ToggleGroup
      value={[value]}
      spacing={1}
      size="sm"
      aria-label={ariaLabel}
      className={cn(
        "shrink-0 rounded-lg border border-border bg-muted/35 p-0.5",
        className,
      )}
      onValueChange={([next]) => {
        if (typeof next === "string" && next !== value)
          onValueChange(next as TValue);
      }}
    >
      {options.map((option) => (
        <ToggleGroupItem
          key={option.value}
          value={option.value}
          disabled={option.disabled}
          className="h-6 min-w-0 rounded-md px-2.5 text-xs font-medium text-muted-foreground transition-[background-color,color,box-shadow] duration-150 hover:text-foreground data-pressed:bg-background data-pressed:text-foreground data-pressed:shadow-sm"
        >
          {option.label}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}
